import { useTranslation } from "react-i18next";
import SmallLoader from "../loaders/SmallLoader";
import EmptyTaskTabState from "./EmptyTaskTabState";
import { ClientTaskType } from "../../types/dtos/tasks/advanced-tasks/ClientTaskType";
import TaskTabItem from "./TaskTabItem";
import { UserContextInterface } from "../../state/UserContext";
import UserTaskListGroupDto from "../../types/dtos/tasks/advanced-tasks/UserTaskListGroupDto";
import TaskRequirementBadge from "./TaskRequirementBadge";
import { ClientTaskTypeCategory } from "../../types/dtos/tasks/advanced-tasks/ClientTaskTypeCategory";
import { CountRestrictionType } from "../../types/dtos/tasks/advanced-tasks/CountRestrictionType";
import { AdvancedTaskStatus } from "../../types/dtos/tasks/advanced-tasks/AdvancedTaskStatus";
import { TaskManagementSectionViewer } from "../../types/dtos/tasks/advanced-tasks/TaskManagementSectionViewer";
import AdvTaskListingDto from "../../types/dtos/dashboards/manager/listings/adv-tasks/AdvTaskListingDto";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import PaginationControls from "../common/PaginationControls";

interface TaskTabProps {
  userContext: UserContextInterface;
  tabType: AdvancedTaskStatus;
  isLoading: boolean;
  taskType: ClientTaskType;
  displayTasks: AdvTaskListingDto[];
  itemsPerPage: number;
  pageNumber: number;
  totalItemCount: number | undefined;
  userGroup?: UserTaskListGroupDto;
  disabledNewTaskButton?: boolean;
  allTasks: AdvTaskListingDto[];
  minTasks: number | null;
  maxTasks: number | null;
  countRestriction: CountRestrictionType;
  activeCategories: ClientTaskTypeCategory[] | null;
  viewerType: TaskManagementSectionViewer;
  isNonEmployeeViewer: boolean;
  onAddNewTaskClick: () => void;
  onExistingTaskClick: (userTaskId: string) => void;
  onPageNumberChange(pageNumber: number): void;
  onPageSizeChange: (pageSize: number) => void;
}

const TaskTab = ({
  userContext,
  tabType,
  isLoading,
  taskType,
  displayTasks,
  itemsPerPage,
  pageNumber,
  totalItemCount,
  userGroup,
  disabledNewTaskButton,
  allTasks,
  minTasks,
  maxTasks,
  countRestriction,
  activeCategories,
  viewerType,
  isNonEmployeeViewer,
  onAddNewTaskClick,
  onExistingTaskClick,
  onPageNumberChange,
  onPageSizeChange = () => {},
}: TaskTabProps) => {
  const { t } = useTranslation();
  const clientColour = userContext.user.client.primaryHexColour;
  const isActiveTabSelected = tabType === "ACTIVE";
  const isCompletedTabSelected = tabType === "COMPLETED";
  const isHistoricTabSelected = tabType === "HISTORIC";
  const doesUserHaveTaskRestrictions =
    minTasks != null ||
    maxTasks != null ||
    activeCategories?.find((x) => x.minTaskCount || x.maxTaskCount) != null;

  // Hide the add new button if the task type is set to suppress the dashboard create and validate button
  // or if the task type is set to hide the add button when in review
  // of is its being viewed from the admin page
  const hideAddNewButton =
    taskType.suppressDashboardCreateAndValidate || viewerType === "ADMIN";

  return (
    <>
      {isLoading && (
        <div className="pt-6">
          <SmallLoader />
          <p className="text-center pt-1 text-[#959595] text-sm">
            {t("Common.Loading")}...
          </p>
        </div>
      )}
      {!isLoading && (
        <>
          {displayTasks == null ||
            (displayTasks.length === 0 && (
              <EmptyTaskTabState
                clientColour={clientColour}
                iconType={taskType.iconType}
                tabType={tabType}
                singularTaskTypeNameIdentifier={
                  taskType.singularNameTranslationKeyIdentifier
                }
                pluralTaskTypeNameIdentifier={
                  taskType.pluralNameTranslationKeyIdentifier
                }
                isActiveTabSelected={isActiveTabSelected}
                userGroup={userGroup}
                hideAddNewButton={hideAddNewButton}
                onAddNewTaskClick={onAddNewTaskClick}
              />
            ))}
          {displayTasks && displayTasks.length > 0 && (
            <>
              {/* Action button bar */}
              {isActiveTabSelected && !hideAddNewButton && (
                <div className="flex justify-between items-center my-4">
                  {doesUserHaveTaskRestrictions && (
                    <div className="flex-grow">
                      <TaskRequirementBadge
                        tasks={allTasks}
                        minTasks={minTasks}
                        maxTasks={maxTasks}
                        countRestriction={countRestriction}
                        activeCategories={activeCategories}
                        taskType={taskType}
                        isNonEmployeeViewer={isNonEmployeeViewer}
                        userGroup={userGroup}
                        origin="OUTSIDE-JOURNEY"
                      />
                    </div>
                  )}
                  <button
                    className="btn-primary text-sm px-3 py-1 flex items-center ml-auto disabled:cursor-not-allowed"
                    onClick={onAddNewTaskClick}
                    disabled={disabledNewTaskButton}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-1" />
                    New {t(taskType.singularNameTranslationKeyIdentifier)}
                  </button>
                </div>
              )}

              {/* Tasks list */}
              <div className={`${isActiveTabSelected ? "" : "mt-4"}`}>
                {displayTasks.map((task) => {
                  return (
                    <TaskTabItem
                      key={task.userTaskId}
                      task={task}
                      isCompletedTabSelected={isCompletedTabSelected}
                      isHistoricTabSelected={isHistoricTabSelected}
                      viewerType={viewerType}
                      onClickEvent={onExistingTaskClick}
                    />
                  );
                })}
              </div>

              {/* Pagination - full width */}
              {totalItemCount && totalItemCount > itemsPerPage && (
                <div className="mt-4">
                  <PaginationControls
                    activePage={pageNumber}
                    itemsPerPage={itemsPerPage}
                    totalItemCount={totalItemCount || 0}
                    onPageChange={onPageNumberChange}
                    onPageSizeChange={onPageSizeChange}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default TaskTab;
